import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Pagination,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  DirectionsRun as DirectionsRunIcon,
  DirectionsBike as DirectionsBikeIcon,
  Pool as PoolIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarToday as CalendarTodayIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  useListWorkoutCoursesQuery,
  useDeleteWorkoutCourseMutation,
  useCreateWorkoutCourseMutation,
  useCreateWorkoutMutation,
} from '../../../store/api/baseApi';
import type { WorkoutCourseResponse } from '../../../store/api/models/workoutModels';

const WorkoutTemplatesPage = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sportTypeFilter, setSportTypeFilter] = useState<string>('all');
  const [intensityFilter, setIntensityFilter] = useState<string>('all');
  const [durationFilter, setDurationFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch templates from API
  const { data: templates = [], isLoading, error } = useListWorkoutCoursesQuery({
    skip: 0,
    limit: 200, // Fetch all for client-side filtering
  });

  const [deleteTemplate] = useDeleteWorkoutCourseMutation();
  const [duplicateTemplate] = useCreateWorkoutCourseMutation();
  const [createWorkout] = useCreateWorkoutMutation();

  // State for "Use Template" dialog
  const [useTemplateDialog, setUseTemplateDialog] = useState<{
    open: boolean;
    template: WorkoutCourseResponse | null;
  }>({ open: false, template: null });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Helper to get sport type from structure
  const getSportTypeFromStructure = (template: WorkoutCourseResponse): string => {
    if (!template.structure || template.structure.length === 0) return 'other';
    const firstStep = template.structure[0];
    if (firstStep.step_type === 'movement') {
      return (firstStep.content as any).activity_type || 'other';
    } else if (firstStep.step_type === 'set') {
      const movements = (firstStep.content as any).movements || [];
      return movements[0]?.activity_type || 'other';
    }
    return 'other';
  };

  // Helper to estimate duration from structure
  const estimateDuration = (template: WorkoutCourseResponse): number => {
    if (!template.structure) return 0;
    let totalSeconds = 0;
    
    template.structure.forEach((step) => {
      if (step.step_type === 'movement') {
        const movement = step.content as any;
        if (movement.focus_type === 'duration') {
          const value = movement.focus_value;
          const unit = movement.focus_unit;
          if (unit === 'seconds') totalSeconds += value;
          else if (unit === 'minutes') totalSeconds += value * 60;
          else if (unit === 'hours') totalSeconds += value * 3600;
        }
      } else if (step.step_type === 'set') {
        const set = step.content as any;
        const setCount = set.set_count || 1;
        set.movements?.forEach((movement: any) => {
          if (movement.focus_type === 'duration') {
            const value = movement.focus_value;
            const unit = movement.focus_unit;
            if (unit === 'seconds') totalSeconds += value * setCount;
            else if (unit === 'minutes') totalSeconds += value * 60 * setCount;
            else if (unit === 'hours') totalSeconds += value * 3600 * setCount;
          }
        });
      }
    });
    
    return Math.round(totalSeconds / 60); // Return minutes
  };

  // Helper to determine intensity (simplified)
  const getIntensity = (template: WorkoutCourseResponse): 'easy' | 'moderate' | 'hard' | 'mixed' => {
    if (!template.structure) return 'moderate';
    const zones: number[] = [];
    
    template.structure.forEach((step) => {
      const extractZones = (movement: any) => {
        if (movement.intensity_focus === 'zone' && movement.intensity_value?.zone) {
          zones.push(movement.intensity_value.zone);
        }
      };
      
      if (step.step_type === 'movement') {
        extractZones(step.content);
      } else if (step.step_type === 'set') {
        (step.content as any).movements?.forEach(extractZones);
      }
    });
    
    if (zones.length === 0) return 'moderate';
    const avgZone = zones.reduce((a, b) => a + b, 0) / zones.length;
    const hasVariety = new Set(zones).size > 2;
    
    if (hasVariety) return 'mixed';
    if (avgZone <= 2) return 'easy';
    if (avgZone <= 3.5) return 'moderate';
    return 'hard';
  };

  // Generate structure preview
  const generateStructurePreview = (template: WorkoutCourseResponse): string => {
    if (!template.structure || template.structure.length === 0) {
      return 'No structure defined';
    }
    
    const parts: string[] = [];
    template.structure.forEach((step) => {
      if (step.step_type === 'movement') {
        const movement = step.content as any;
        const value = movement.focus_value;
        const unit = movement.focus_unit;
        const zone = movement.intensity_value?.zone || '?';
        parts.push(`${value}${unit} Z${zone}`);
      } else if (step.step_type === 'set') {
        const set = step.content as any;
        const count = set.set_count;
        const movementDesc = set.movements?.map((m: any) => 
          `${m.focus_value}${m.focus_unit}`
        ).join(' + ') || 'movements';
        parts.push(`${count}x(${movementDesc})`);
      }
    });
    
    return parts.join(' → ');
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    
    const sportType = getSportTypeFromStructure(template);
    const matchesSport = sportTypeFilter === 'all' || sportType === sportTypeFilter;
    
    const intensity = getIntensity(template);
    const matchesIntensity = intensityFilter === 'all' || intensity === intensityFilter;
    
    const duration = estimateDuration(template);
    const matchesDuration = durationFilter === 'all' ||
      (durationFilter === 'short' && duration <= 45) ||
      (durationFilter === 'medium' && duration > 45 && duration <= 90) ||
      (durationFilter === 'long' && duration > 90);
    
    return matchesSearch && matchesSport && matchesIntensity && matchesDuration;
  });

  const itemsPerPage = 12;
  const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);
  const paginatedTemplates = filteredTemplates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getSportIcon = (sportType: string) => {
    switch (sportType) {
      case 'run':
        return <DirectionsRunIcon />;
      case 'bike':
        return <DirectionsBikeIcon />;
      case 'swim':
        return <PoolIcon />;
      default:
        return <FitnessCenterIcon />;
    }
  };

  const getIntensityColor = (intensity: string) => {
    switch (intensity) {
      case 'easy':
        return 'success';
      case 'moderate':
        return 'warning';
      case 'hard':
        return 'error';
      case 'mixed':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'grid' | 'list' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleSportTypeChange = (event: SelectChangeEvent) => {
    setSportTypeFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleIntensityChange = (event: SelectChangeEvent) => {
    setIntensityFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleDurationChange = (event: SelectChangeEvent) => {
    setDurationFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleCreateTemplate = () => {
    navigate('/trainer/templates/create');
  };

  const handleEditTemplate = (templateId: number) => {
    navigate(`/trainer/templates/edit/${templateId}`);
  };

  const handleUseTemplate = (template: WorkoutCourseResponse) => {
    setUseTemplateDialog({ open: true, template });
  };

  const handlePlanInCalendar = () => {
    if (useTemplateDialog.template) {
      // Navigate to calendar with template ID in URL so it can be pre-selected
      navigate(`/athlete/workouts?templateId=${useTemplateDialog.template.id}`);
    }
    setUseTemplateDialog({ open: false, template: null });
  };

  const handleAssignToActivity = async () => {
    if (!useTemplateDialog.template) return;

    try {
      // Create workout with null scheduled_at (for manual assignment to activities)
      await createWorkout({
        name: useTemplateDialog.template.name,
        sport_type: useTemplateDialog.template.sport_type,
        description: useTemplateDialog.template.description || '',
        course_id: useTemplateDialog.template.id,
        scheduled_at: null,
        notes: 'Created from template for activity assignment',
        priority: 'medium',
      }).unwrap();

      setSnackbar({
        open: true,
        message: 'Workout created successfully! You can now assign it to activities.',
        severity: 'success',
      });
      setUseTemplateDialog({ open: false, template: null });

      // Navigate to workouts list or activities page
      navigate('/athlete/activities');
    } catch (error) {
      console.error('Failed to create workout:', error);
      setSnackbar({
        open: true,
        message: 'Failed to create workout. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteTemplate(templateId).unwrap();
      } catch (error) {
        console.error('Failed to delete template:', error);
      }
    }
  };

  const handleDuplicateTemplate = async (template: WorkoutCourseResponse) => {
    try {
      await duplicateTemplate({
        name: `${template.name} (Copy)`,
        sport_type: template.sport_type,
        description: template.description,
        structure: template.structure,
      }).unwrap();
    } catch (error) {
      console.error('Failed to duplicate template:', error);
    }
  };

  const renderTemplateCard = (template: WorkoutCourseResponse) => {
    const sportType = getSportTypeFromStructure(template);
    const intensity = getIntensity(template);
    const duration = estimateDuration(template);
    const structurePreview = generateStructurePreview(template);

    return (
      <Card
        key={template.id}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ mr: 1, color: 'primary.main' }}>
              {getSportIcon(sportType)}
            </Box>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {template.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {template.description || 'No description'}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Chip
              label={intensity}
              color={getIntensityColor(intensity) as any}
              size="small"
              sx={{ mr: 1 }}
            />
            <Chip
              label={`${duration} min`}
              variant="outlined"
              size="small"
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Structure:
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {structurePreview}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleUseTemplate(template)}
          >
            Use
          </Button>
          <Box>
            <IconButton
              size="small"
              onClick={() => handleEditTemplate(template.id)}
              title="Edit"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDuplicateTemplate(template)}
              title="Duplicate"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteTemplate(template.id)}
              title="Delete"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Workout Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTemplate}
          size="large"
        >
          Create New Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load templates. Please try again.
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sport Type</InputLabel>
              <Select
                value={sportTypeFilter}
                label="Sport Type"
                onChange={handleSportTypeChange}
                startAdornment={<FilterListIcon sx={{ ml: 1, mr: -0.5 }} />}
              >
                <MenuItem value="all">All Sports</MenuItem>
                <MenuItem value="run">Run</MenuItem>
                <MenuItem value="bike">Bike</MenuItem>
                <MenuItem value="swim">Swim</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Intensity</InputLabel>
              <Select
                value={intensityFilter}
                label="Intensity"
                onChange={handleIntensityChange}
              >
                <MenuItem value="all">All Intensities</MenuItem>
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Duration</InputLabel>
              <Select
                value={durationFilter}
                label="Duration"
                onChange={handleDurationChange}
              >
                <MenuItem value="all">All Durations</MenuItem>
                <MenuItem value="short">&lt; 45 min</MenuItem>
                <MenuItem value="medium">45-90 min</MenuItem>
                <MenuItem value="long">&gt; 90 min</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="grid" aria-label="grid view">
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="100%" />
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Skeleton variant="rectangular" width={60} height={24} />
                    <Skeleton variant="rectangular" width={60} height={24} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          {filteredTemplates.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No templates found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your filters or create a new template
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTemplate}
              >
                Create First Template
              </Button>
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                {paginatedTemplates.map((template) => (
                  <Grid
                    item
                    xs={12}
                    sm={viewMode === 'grid' ? 6 : 12}
                    md={viewMode === 'grid' ? 4 : 12}
                    key={template.id}
                  >
                    {renderTemplateCard(template)}
                  </Grid>
                ))}
              </Grid>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_event, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Use Template Dialog */}
      <Dialog
        open={useTemplateDialog.open}
        onClose={() => setUseTemplateDialog({ open: false, template: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Use Template: {useTemplateDialog.template?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            How would you like to use this template?
          </Typography>
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handlePlanInCalendar}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 2,
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.light',
                  },
                }}
              >
                <ListItemIcon>
                  <CalendarTodayIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Plan in Calendar"
                  secondary="Schedule this workout for a specific date"
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleAssignToActivity}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.light',
                  },
                }}
              >
                <ListItemIcon>
                  <AssignmentIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Assign to Activity"
                  secondary="Create workout to link with existing activities"
                />
              </ListItemButton>
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseTemplateDialog({ open: false, template: null })}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WorkoutTemplatesPage;